/**
 * Redaction primitive for Heap autocapture.
 *
 * Wraps a subtree so Heap's autocapture cannot read its text (and, optionally,
 * named attribute values). Used to fence off PII-dense fields — addresses,
 * prices, EMD, party names, contract file names — at the point of render.
 *
 * Uses Heap's documented redaction attributes. BOTH cascade to descendants —
 * placing either one on a wrapper covers the entire subtree below it:
 *  - `data-heap-redact-text` — Heap replaces the element's (and descendants')
 *    target text with `****` before it leaves the browser. Interactions
 *    (pageview, click) are still captured; only the sensitive text is masked.
 *  - `data-heap-redact-attributes="attr1,attr2"` — Heap redacts the listed
 *    attribute values (replaced with `***`) on the element AND its descendants
 *    before they leave the browser. (Heap's canonical example puts this on an
 *    outer `<div>` and it redacts attrs on a `<p>` two levels down.) So putting
 *    `redactAttributes` on a wrapper covers value/href/etc. on nested
 *    `<input>`/`<a>` elements too — no per-field marker needed. NOTE: Heap
 *    cannot redact the `id` attribute this way (use `heap-ignore` for that).
 *
 * Source: https://developers.heap.io/docs/hide-sensitive-data and
 * https://developers.heap.io/docs/ignoring-sensitive-data-and-pii
 *
 * This is purely structural — it works whether or not Heap is loaded, and is a
 * no-op for the user (renders its children unchanged). It is the single
 * greppable thing to wrap a sensitive field with.
 */

import type { CSSProperties, ElementType, ReactNode } from 'react'

export interface RedactProps {
  /** The content to fence off from Heap text autocapture. */
  children: ReactNode
  /**
   * Element to render. Defaults to `span` (inline, layout-neutral). Pass `div`
   * or any host element when block layout is required.
   */
  as?: ElementType
  /**
   * Named attributes to additionally redact (comma-joined into
   * `data-heap-redact-attributes`). Omit for the common text-only case.
   * The `id` attribute cannot be redacted this way (Heap limitation).
   *
   * Heap's `data-heap-redact-attributes` cascades to descendants (same as
   * `data-heap-redact-text`), so placing `redactAttributes` on a wrapper
   * redacts the listed attributes across the whole subtree — including
   * `value` on nested `<input>`s and `href`/`alt`/`title` on nested `<a>`/
   * `<img>` elements. No per-field marker is needed; mark the wrapper once.
   */
  redactAttributes?: readonly string[]
  /** Passthrough class for layout/styling parity with the wrapped node. */
  className?: string
  /** Passthrough inline style for layout parity with the wrapped node. */
  style?: CSSProperties
}

/**
 * Mark `children` so Heap autocapture cannot read their text. Optionally also
 * redact named attribute values via `redactAttributes`.
 */
export function Redact({ children, as, redactAttributes, className, style }: RedactProps) {
  const Component = (as ?? 'span') as ElementType
  const attributeList =
    redactAttributes && redactAttributes.length > 0 ? redactAttributes.join(',') : undefined

  return (
    <Component
      data-heap-redact-text="true"
      data-heap-redact-attributes={attributeList}
      className={className}
      style={style}
    >
      {children}
    </Component>
  )
}
